import { forwardRef } from "react";
import { NavLink } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { Tooltip } from "@/components/ui";
import { cn } from "@/lib/cn";

export interface SidebarItemProps {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  collapsed?: boolean;
}

/**
 * SidebarItem — Anthropic redesign primitive (Sprint R2).
 *
 * Spec (plan 6.1):
 *  - Expanded: 44px height, px-3 gap-3, icon 20px + label 15px medium
 *    (active 600), o'qilishi oson; hover: bg-[var(--bg-surface-hover)]
 *  - active:
 *      • chap chetda 2px rail, height 60% vertically centered, color brand-primary
 *      • bg-[var(--brand-soft)]
 *      • label color brand-primary, weight 600
 *      • icon color brand-primary
 *  - collapsed (64px sidebar) — faqat icon, 40x40 tap target (h-10 w-10),
 *    ikonka 22px, rail to'liq kengligida markazda; label Tooltip orqali (o'ngda)
 *  - keyboard focus: ring brand-primary
 */
export const SidebarItem = forwardRef<HTMLAnchorElement, SidebarItemProps>(
  ({ to, label, icon: Icon, end, collapsed = false }, ref) => {
    const link = (
      <NavLink
        ref={ref}
        to={to}
        end={end}
        aria-label={label}
        className={({ isActive }) =>
          cn(
            "group relative flex items-center gap-3 rounded-md transition-colors",
            "text-[15px] font-medium text-[var(--text-secondary)]",
            "hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]/60",
            // Collapsed: 40x40 tap target, rail to'liq kengligida markazda
            collapsed ? "mx-auto h-10 w-10 justify-center px-0" : "h-11 px-3",
            isActive &&
              (collapsed
                ? "bg-[var(--brand-soft)] text-[var(--brand-primary)]"
                : "bg-[var(--brand-soft)] font-semibold text-[var(--brand-primary)]"),
          )
        }
      >
        {({ isActive }) => (
          <>
            {/* Active rail — chap chetda 2px coral, height 60% vertically centered */}
            {isActive && !collapsed && (
              <span
                aria-hidden
                className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-[60%] rounded-r-sm bg-[var(--brand-primary)]"
              />
            )}
            <Icon
              className={cn(
                "shrink-0 transition-colors",
                // Collapsed ikonkalar 22px, expanded 20px
                collapsed ? "h-[22px] w-[22px]" : "h-5 w-5",
                isActive
                  ? "text-[var(--brand-primary)]"
                  : "text-[var(--text-muted)] group-hover:text-[var(--text-primary)]",
              )}
              aria-hidden
            />
            {!collapsed && <span className="truncate">{label}</span>}
          </>
        )}
      </NavLink>
    );

    // Yig'ilgan holatda label faqat hover tooltip orqali ko'rinadi (o'ngda).
    if (collapsed) {
      return (
        <Tooltip content={label} side="right">
          {link}
        </Tooltip>
      );
    }

    return link;
  },
);
SidebarItem.displayName = "SidebarItem";
